import { isDefined } from "@clipboard-health/util-ts";

import { type GroupedShiftCounts, type ShiftCardData, type TimeSlotCounts } from "../types";

interface GroupedShifts {
  am: ShiftCardData[];
  pm: ShiftCardData[];
  noc: ShiftCardData[];
  ori: ShiftCardData[];
}

/**
 * Groups shift cards by time slot (AM, PM, NOC, ORI). Shifts with
 * `orientationShiftType` set route into the synthetic "ori" bucket
 * regardless of their underlying am/pm/noc `timeSlot`.
 */
export function groupShiftsByTimeSlot(shifts: ShiftCardData[]): GroupedShifts {
  const am: ShiftCardData[] = [];
  const pm: ShiftCardData[] = [];
  const noc: ShiftCardData[] = [];
  const ori: ShiftCardData[] = [];

  for (const shift of shifts) {
    if (isDefined(shift.orientationShiftType)) {
      ori.push(shift);
      continue;
    }
    switch (shift.timeSlot) {
      case "am": {
        am.push(shift);
        break;
      }

      case "pm": {
        pm.push(shift);
        break;
      }

      case "noc": {
        noc.push(shift);
        break;
      }

      default: {
        // Unknown time slot, skip
        break;
      }
    }
  }

  return { am, pm, noc, ori };
}

/**
 * Calculates counts for a single time slot
 */
export function calculateTimeSlotCounts(shifts: ShiftCardData[]): TimeSlotCounts {
  let filledShiftsCount = 0;
  let unfilledShiftsCount = 0;

  for (const shift of shifts) {
    if (shift.workerId) {
      filledShiftsCount += 1;
    } else {
      unfilledShiftsCount += 1;
    }
  }

  // Note: warningShiftsCount not currently implemented
  return { filledShiftsCount, unfilledShiftsCount, warningShiftsCount: 0 };
}

/**
 * Calculates counts from grouped shifts
 */
export function calculateCountsFromShifts(groupedShifts: GroupedShifts): GroupedShiftCounts {
  return {
    am: calculateTimeSlotCounts(groupedShifts.am),
    pm: calculateTimeSlotCounts(groupedShifts.pm),
    noc: calculateTimeSlotCounts(groupedShifts.noc),
    ori: calculateTimeSlotCounts(groupedShifts.ori),
  };
}
